import React from 'react';
import {
  AppBar, Toolbar, Typography, Button, Box, Menu, MenuItem,
  IconButton, useTheme, useMediaQuery, Drawer, List, ListItem,
  ListItemIcon, ListItemText, Collapse
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu as MenuIcon, Home, School, Business, AdminPanelSettings,
  BarChart, AccountCircle, Login, Logout, Article, Favorite,
  Phone, Email, ExpandMore, ExpandLess, Close, KeyboardArrowDown
} from '@mui/icons-material';
import { useAuth } from '../App';
import Cookies from 'js-cookie';

const Navbar = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = React.useState(null);
  const [adminMenuAnchor, setAdminMenuAnchor] = React.useState(null);
  const [mobileAdminOpen, setMobileAdminOpen] = React.useState(false);

  const handleLogout = () => {
    setAccountMenuAnchor(null);
    setAdminMenuAnchor(null);
    setMobileDrawerOpen(false);

    const cookiesToClear = ['idToken', 'refreshToken', 'userEmail', 'userRole', 'uid'];
    cookiesToClear.forEach(name => {
      Cookies.remove(name);
      Cookies.remove(name, { path: '/' });
    });

    localStorage.clear();
    sessionStorage.clear();
    window.location.replace('/');
  };

  const handleOpenAccountMenu = (event) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleCloseAccountMenu = () => {
    setAccountMenuAnchor(null);
  };

  const handleOpenAdminMenu = (event) => {
    setAdminMenuAnchor(event.currentTarget);
  };

  const handleCloseAdminMenu = () => {
    setAdminMenuAnchor(null);
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const toggleMobileAdmin = () => {
    setMobileAdminOpen(!mobileAdminOpen);
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { label: 'Home', path: '/', icon: <Home />, public: true },
    { label: 'Database', path: '/database', icon: <School />, roles: ['Admin', 'View Only'] },
    { label: 'Contact', path: '/contact', icon: <Article />, public: true },
    { label: 'Statistics', path: '/statistics', icon: <BarChart />, public: true },
  ];

  const externalLinks = [
    { label: 'News', url: 'https://usc.edu.tt/', icon: <Article /> },
    { label: 'Donate', url: 'https://usc.edu.tt/give', icon: <Favorite /> },
  ];

  const adminMenuItems = [
    { label: 'Alumni Requests', path: '/admin/add-requests' },
    { label: 'Business Requests', path: '/admin/business-requests' },
    { label: 'Data Entry', path: '/admin/data-entry' },
    { label: 'User Management', path: '/admin/users' },
    //{ label: 'Manage News', path: '/admin/news' },
    { label: 'Customize Homepage', path: '/admin/customize-homepage' },
    { label: 'Form Options', path: '/admin/form-options' },
    { label: 'Network Notifications', path: '/admin/network' },
    { label: 'SOP Guides', path: '/admin/sops' },
    { label: 'Activity Log', path: '/admin/activity-log' },
    { label: 'Alumni ID Cleanup', path: '/admin/alumni-id-cleanup' },
  ];

  const canSeeItem = (item) => {
    if (item.public) return true;
    if (!user) return false;
    if (!item.roles) return true;
    return item.roles.includes(user.role);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#1A1A1A',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 0,
          m: 0,
          p: 0
        }}
      >
        {/* REMOVED the Box wrapper that was centering everything */}
        <Toolbar
          sx={{
            minHeight: { xs: 70, md: 100 },
            justifyContent: 'space-between',
            px: { xs: 2, md: 4 },  // Direct padding on toolbar
            m: 0
          }}
        >
          {/* Logo - FAR LEFT */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              '&:hover': { opacity: 0.8 }
            }}
          >
            <img
              src="/usc-logo.png"
              alt="USC Alumni"
              style={{
                height: isMobile ? '80px' : '120px',
                width: 'auto',
                display: 'block'
              }}
            />
          </Box>

          {/* Desktop Navigation - FAR RIGHT */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {navItems.filter(canSeeItem).map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: isActive(item.path) ? '#FACA18' : 'white',
                    fontSize: '1.20rem',
                    fontWeight: 700,
                    px: 2,
                    py: 1,
                    textTransform: 'none',
                    borderRadius: 0,
                    '&:hover': {
                      color: '#FACA18',
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}

              {externalLinks.map((link) => (
                <Button
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'white',
                    fontSize: '1.20rem',
                    fontWeight: 700,
                    px: 2,
                    py: 1,
                    textTransform: 'none',
                    borderRadius: 0,
                    '&:hover': {
                      color: '#FACA18',
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  {link.label}
                </Button>
              ))}

              {user?.role === 'Admin' && (
                <>
                  <Button
                    endIcon={<KeyboardArrowDown />}
                    onClick={handleOpenAdminMenu}
                    sx={{
                      color: 'white',
                      fontSize: '1.20rem',
                      fontWeight: 700,
                      px: 2,
                      py: 1,
                      textTransform: 'none',
                      borderRadius: 0,
                      '&:hover': {
                        color: '#FACA18',
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    Admin
                  </Button>
                  <Menu
                    anchorEl={adminMenuAnchor}
                    open={Boolean(adminMenuAnchor)}
                    onClose={handleCloseAdminMenu}
                    PaperProps={{
                      sx: { borderRadius: 0 }
                    }}
                  >
                    {adminMenuItems.map((item) => (
                      <MenuItem
                        key={item.path}
                        component={Link}
                        to={item.path}
                        onClick={handleCloseAdminMenu}
                        sx={{ fontSize: '0.9rem', fontWeight: 600 }}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}

              {/* Account Menu */}
              {user ? (
                <>
                  <Button
                    startIcon={<AccountCircle />}
                    endIcon={<KeyboardArrowDown />}
                    onClick={handleOpenAccountMenu}
                    sx={{
                      color: 'white',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      ml: 1,
                      textTransform: 'none',
                      borderRadius: 0,
                      '&:hover': {
                        color: '#FACA18',
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    {user?.email?.split('@')[0]}
                  </Button>
                  <Menu
                    anchorEl={accountMenuAnchor}
                    open={Boolean(accountMenuAnchor)}
                    onClose={handleCloseAccountMenu}
                    PaperProps={{
                      sx: { borderRadius: 0 }
                    }}
                  >
                    <MenuItem disabled>
                      <Typography variant="caption" color="text.secondary">
                        Role: {user.role}
                      </Typography>
                    </MenuItem>
                    {user.role === 'Alumni' && (
                      <>
                        <MenuItem component={Link} to="/profile" onClick={handleCloseAccountMenu} sx={{ fontWeight: 600 }}>
                          My Profile
                        </MenuItem>
                        <MenuItem component={Link} to="/alumni-id" onClick={handleCloseAccountMenu} sx={{ fontWeight: 600 }}>
                          Alumni ID Card
                        </MenuItem>
                      </>
                    )}
                    <MenuItem onClick={handleLogout} sx={{ fontWeight: 600 }}>
                      <Logout sx={{ mr: 1, fontSize: 18 }} /> Logout
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button
                  component={Link}
                  to="/login"
                  startIcon={<Login />}
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    ml: 1,
                    textTransform: 'none',
                    borderRadius: 0,
                    '&:hover': {
                      color: '#FACA18',
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  Login
                </Button>
              )}
            </Box>
          )}

          {/* Mobile Menu Button - FAR RIGHT */}
          {isMobile && (
            <IconButton
              color="inherit"
              onClick={toggleMobileDrawer}
              sx={{
                color: 'white',
                borderRadius: 0
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer - keeping same as before */}
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={toggleMobileDrawer}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: '#1A1A1A',
            color: 'white',
            borderRadius: 0,
            '& .MuiListItemText-primary': {
              color: 'white',
              fontWeight: 700
            },
            '& .MuiListItemText-secondary': {
              color: 'rgba(255,255,255,0.7)'
            },
            '& .MuiTypography-root': {
              color: 'white'
            }
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Menu</Typography>
          <IconButton onClick={toggleMobileDrawer} sx={{ color: 'white', borderRadius: 0 }}>
            <Close />
          </IconButton>
        </Box>

        <List>
          {user && (
            <ListItem sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {user.email?.split('@')[0]}
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.6)">
                {user.role}
              </Typography>
            </ListItem>
          )}

          {navItems.filter(canSeeItem).map((item) => (
            <ListItem
              button
              key={item.path}
              component={Link}
              to={item.path}
              onClick={toggleMobileDrawer}
              sx={{
                py: 1.5,
                borderRadius: 0,
                '&:hover': { bgcolor: '#FACA18' },
                bgcolor: isActive(item.path) ? '#FACA18' : 'transparent'
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItem>
          ))}

          {externalLinks.map((link) => (
            <ListItem
              button
              key={link.url}
              component="a"
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={toggleMobileDrawer}
              sx={{
                py: 1.5,
                borderRadius: 0,
                '&:hover': { bgcolor: '#FACA18' }
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {link.icon}
              </ListItemIcon>
              <ListItemText primary={link.label} />
            </ListItem>
          ))}

          {user?.role === 'Admin' && (
            <>
              <ListItem
                button
                onClick={toggleMobileAdmin}
                sx={{
                  py: 1.5,
                  borderRadius: 0,
                  '&:hover': { bgcolor: '#FACA18' }
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  <AdminPanelSettings />
                </ListItemIcon>
                <ListItemText primary="Admin" />
                {mobileAdminOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse in={mobileAdminOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {adminMenuItems.map((item) => (
                    <ListItem
                      button
                      key={item.path}
                      component={Link}
                      to={item.path}
                      onClick={toggleMobileDrawer}
                      sx={{
                        pl: 4,
                        py: 1,
                        borderRadius: 0,
                        '&:hover': { bgcolor: '#FACA18' }
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {user?.role === 'Alumni' && (
            <>
              <ListItem
                button
                component={Link}
                to="/profile"
                onClick={toggleMobileDrawer}
                sx={{
                  py: 1.5,
                  borderRadius: 0,
                  '&:hover': { bgcolor: '#FACA18' }
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  <AccountCircle />
                </ListItemIcon>
                <ListItemText primary="My Profile" />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/alumni-id"
                onClick={toggleMobileDrawer}
                sx={{
                  py: 1.5,
                  borderRadius: 0,
                  '&:hover': { bgcolor: '#FACA18' }
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  <School />
                </ListItemIcon>
                <ListItemText primary="Alumni ID Card" />
              </ListItem>
            </>
          )}

          {user ? (
            <ListItem
              button
              onClick={handleLogout}
              sx={{
                py: 1.5,
                mt: 2,
                borderTop: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 0,
                '&:hover': { bgcolor: '#FACA18' }
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                <Logout />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          ) : (
            <ListItem
              button
              component={Link}
              to="/login"
              onClick={toggleMobileDrawer}
              sx={{
                py: 1.5,
                mt: 2,
                borderTop: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 0,
                '&:hover': { bgcolor: '#FACA18' }
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                <Login />
              </ListItemIcon>
              <ListItemText primary="Login" />
            </ListItem>
          )}
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;